import { ArrowRight } from "lucide-react";
import { Reveal, Eyebrow, SectionHeading } from "@/components/Reveal";
import { useDemoForm } from "@/components/DemoForm";

const TRADITIONAL = [
  { step: "Recall the encounter", time: "≈5 min" },
  { step: "Organize the information", time: "≈4 min" },
  { step: "Structure the note", time: "≈4 min" },
  { step: "Type the documentation", time: "≈8 min" },
  { step: "Review", time: "≈3 min" },
];

const VERACHART = [
  { step: "Provide de-identified bullet points", time: "≈2 min" },
  { step: "VeraChart prompt structures the note", time: "≈30 sec" },
  { step: "Staff review", time: "≈2.5 min" },
];

function WorkflowCard({ title, total, steps, accent, barWidth }) {
  const isTeal = accent === "teal";
  return (
    <div className="rounded-2xl border border-navy-700 bg-navy-800 p-7 sm:p-8">
      <div className="flex items-baseline justify-between gap-4">
        <h3 className="font-display text-lg font-bold text-white">{title}</h3>
        <span className={`font-mono text-2xl font-bold ${isTeal ? "text-teal-400" : "text-red-400"}`}>{total}</span>
      </div>
      <div className="mt-5 h-2.5 w-full overflow-hidden rounded-full bg-navy-700" aria-hidden="true">
        <div className={`h-full rounded-full ${isTeal ? "bg-teal-400" : "bg-red-400/80"}`} style={{ width: barWidth }} />
      </div>
      <ol className="mt-6 space-y-0">
        {steps.map((s, i) => (
          <li key={s.step} className="relative flex items-center justify-between gap-4 border-l-2 border-navy-700 py-3.5 pl-5">
            <span className={`absolute -left-[5px] top-1/2 h-2 w-2 -translate-y-1/2 rounded-full ${isTeal ? "bg-teal-400" : "bg-red-400/80"}`} aria-hidden="true" />
            <span className="text-sm text-slate-200">{s.step}</span>
            <span className="font-mono text-xs text-slate-400">{s.time}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

export function Comparison() {
  const { openDialog } = useDemoForm();
  return (
    <section id="demonstration" className="bg-navy py-20 lg:py-28" aria-labelledby="comparison-heading">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-2xl">
          <Eyebrow dark>Demonstration Results</Eyebrow>
          <SectionHeading id="comparison-heading" dark>See the Difference in the Workflow</SectionHeading>
          <p className="mt-5 text-base leading-relaxed text-slate-300">
            The same progress-note workflow, completed two ways — recorded side by side.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-6 lg:grid-cols-2" data-testid="workflow-comparison">
          <Reveal delay={0.05}>
            <WorkflowCard title="Traditional Documentation" total="≈ 24 min" steps={TRADITIONAL} accent="red" barWidth="100%" />
          </Reveal>
          <Reveal delay={0.15}>
            <WorkflowCard title="VeraChart-Assisted Workflow" total="≈ 5 min" steps={VERACHART} accent="teal" barWidth="21%" />
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <div className="mt-12 flex flex-col items-start justify-between gap-8 rounded-2xl border border-teal-600/30 bg-teal-600/10 p-8 lg:flex-row lg:items-center">
            <div>
              <p className="font-mono text-4xl font-bold text-teal-400 sm:text-5xl" data-testid="minutes-returned-stat">≈ 19 min</p>
              <p className="mt-2 text-sm text-slate-300">returned per note in our demonstration</p>
            </div>
            <button
              type="button"
              data-testid="comparison-request-demo-button"
              onClick={() => openDialog("comparison")}
              className="inline-flex items-center gap-2 rounded-full bg-teal-600 px-8 py-3.5 font-display text-base font-semibold text-white shadow-lg transition-all hover:-translate-y-0.5 hover:bg-teal-500"
            >
              Request the Demonstration <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </Reveal>


        <Reveal delay={0.1}>
          <div className="mt-14" data-testid="before-after-text">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.22em] text-teal-400">The Receipts</p>
            <h3 className="mt-3 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
              Same Team Member. Same Kind of Note. Two Ways of Writing It.
            </h3>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-300">
              The verbatim PIE narrative from a real side-by-side run — a fully fictional training note (made-up
              client, goals, and interventions on a plain document template; no real person, no real EHR). Notice the
              terminology — the prompt was configured for "member" language, and the VeraChart-assisted note
              structures the documented goals, interventions, responses, and next steps into a consistent PIE format.
            </p>
            <div className="mt-8 grid gap-6 lg:grid-cols-2">
              {[
                {
                  href: "/samples/traditional-typed-progress-note.pdf",
                  title: "Typed by hand",
                  time: "24+ min",
                  accent: "text-red-400",
                  border: "border-red-400/30",
                  testid: "before-after-traditional",
                  sections: [
                    { k: "P", t: "CPSS met client at local library for weekly scheduled check in. Client stated, \u201cI feel like I'm falling back into old habits and can't stay on track\u201d. Noted low energy and visible anxiety when discussing upcoming routine changes. Client also expressed feeling overwhelmed and isolated over the past week." },
                    { k: "I", t: "CPSS utilized active listening and reflective dialogue to validate client's feelings without judgment. CPSS shared lived experience regarding managing routine disruptions and navigating self-doubt in early recovery. Explored existing natural supports and reviewed client's daily coping toolbox. Focused on boundary setting and small, manageable daily goals." },
                    { k: "E", t: "Client's posture was relaxed and affect shifted from tense to engaged. Client identified two concrete steps to take before the next session: reach out to support friend twice this week and taking a daily 15-minute walk. Agreed to reconnect next Tuesday at 10:00 am to review his progress." },
                  ],
                },
                {
                  href: "/samples/verachart-prompt-progress-note.pdf",
                  title: "Built with one VeraChart prompt",
                  time: "≈ 5 min",
                  accent: "text-teal-400",
                  border: "border-teal-500/40",
                  testid: "before-after-prompt",
                  sections: [
                    { k: "P", t: "CPSS met with member at the local community library for a one hour scheduled face-to-face contact to address recovery maintenance and coping strategies surrounding his severe and persistent MI. Member reported feeling like he is falling back into old habits and expressed distress that he cannot stay on track with his daily recovery routine. Member presented with noticeably low physical energy, psychomotor sluggishness, and visible anxiety characterized by restlessness and tense body language. Member endorsed distress regarding maintaining structure, managing internal urges, and staying connected to his recovery network. Member denied any current SI/HI or AVH during the contact." },
                    { k: "I", t: "CPSS engaged member by utilizing open-ended questions to explore member's current feelings regarding his daily routine and recent setbacks. CPSS used purposeful self-disclosure by sharing lived experience regarding managing personal anxiety around major schedule changes and navigating self-doubt during early recovery to normalize member's feelings. CPSS provided reflective listening to help member explore ambivalence and develop discrepancy between his stated desire for long-term stability and his current patterns. CPSS delivered genuine affirmations regarding member's willingness to reach out, his self-awareness, and his past successes in maintaining sobriety and mental wellness. CPSS focused the discussion by evoking change talk around member's intrinsic motivators and personal strengths. CPSS reviewed member's natural supports and guided member in identifying which positive relationships foster accountability. CPSS facilitated an exploration of member's urge management tools to identify actionable coping skills. CPSS collaborated with member during the planning phase to establish healthy interpersonal boundaries and build realistic, recovery-oriented daily goals. CPSS assisted member in formulating an action step to complete a daily 15-minute walk for physical regulation and emotional relief. CPSS guided member in planning scheduled phone check-ins with supportive peers at least twice per week to strengthen his social safety net. CPSS summarized the key commitments made during the session and collaborated with member to schedule the next ACT face-to-face appointment for next Tuesday at 10:00 a.m." },
                    { k: "E", t: "Member actively participated throughout the meeting, initially presenting with flat affect, guarded body posture, and low verbal output before showing noticeable ease and openness. Member responded favorably to CPSS sharing lived experience, acknowledging that hearing similar recovery challenges helped reduce his sense of isolation. Member engaged with the reflective statements and recognized the contrast between his long-term wellness goals and his current isolation tendencies. Member received affirmations with increased eye contact and verbalized appreciation for having his past progress validated. Member demonstrated change talk by identifying his desire to regain control over his daily schedule rather than succumbing to old habits. Member identified several positive personal relationships that offer genuine encouragement and accountability. Member evaluated his urge coping strategies, recognizing which tools have proven effective in previous crisis situations. Member agreed to set firm personal boundaries with individuals who trigger his urge to revert to unhealthy behaviors. Member committed to integrating a 15-minute daily walk into his routine to reduce stress and improve energy levels. Member agreed to initiate outbound contact with trusted support friends at least two times weekly, identifying this step as a realistic approach to counteracting isolation. Member exhibited visible relaxation, marked by relaxed shoulder tension, improved conversational flow, and an uplifted demeanor by the conclusion of the session. Member confirmed his agreement with the recovery plan and agreed to the next scheduled visit on Tuesday at 10:00 a.m." },
                  ],
                },
              ].map((doc) => (
                <div key={doc.title} className={`rounded-2xl border ${doc.border} bg-navy-800`} data-testid={doc.testid}>
                  <div className="flex items-center justify-between gap-4 border-b border-navy-700 px-6 py-4">
                    <p className="font-display text-base font-bold text-white">{doc.title}</p>
                    <div className="flex items-center gap-4">
                      <span className={`font-mono text-sm font-bold ${doc.accent}`}>{doc.time}</span>
                      <a
                        href={doc.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`${doc.testid}-pdf-link`}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-slate-300 underline decoration-teal-500 underline-offset-2 transition-colors hover:text-teal-300"
                      >
                        Full document (PDF) <ArrowRight className="h-3 w-3" aria-hidden="true" />
                      </a>
                    </div>
                  </div>
                  <div className="max-h-[420px] space-y-5 overflow-y-auto px-6 py-6">
                    {doc.sections.map((s) => (
                      <div key={s.k}>
                        <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-teal-400">
                          {s.k} — {s.k === "P" ? "Problem / Presenting Situation" : s.k === "I" ? "Intervention" : "Evaluation / Response & Plan"}
                        </p>
                        <p className="mt-2 text-sm leading-relaxed text-slate-300">{s.t}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <p className="mt-6 max-w-3xl text-xs leading-relaxed text-slate-400">
            This comparison reflects VeraChart's side-by-side demonstration with a fictional training note. It should
            not be interpreted as a guarantee of identical results for every user or organization.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
